import { Directive, Injector, TemplateRef, ViewChild } from "@angular/core";
import { Zone } from "@common/ADAPT.Common.Model/methodology/zone";
import { TeamLocation } from "@common/ADAPT.Common.Model/organisation/team-location";
import { TeamTypeCode } from "@common/ADAPT.Common.Model/organisation/team-type-code";
import { TeamTypeConfiguration } from "@common/ADAPT.Common.Model/organisation/team-type-configuration";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { IBreezeEntity } from "@common/lib/data/breeze-entity.interface";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { ArrayUtilities } from "@common/lib/utilities/array-utilities";
import { ErrorHandlingUtilities } from "@common/lib/utilities/error-handling-utilities";
import { ObjectUtilities } from "@common/lib/utilities/object-utilities";
import { SortUtilities } from "@common/lib/utilities/sort-utilities";
import { ORGANISATION_CONFIGURATION_PAGE } from "@common/page-route-providers";
import { IAdaptRoute } from "@common/route/page-route-builder";
import { UserService } from "@common/user/user.service";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { ChangeAction } from "@common/ux/change-manager/change-manager.service";
import { UnsavedChangesDialogComponent } from "@common/ux/change-manager/unsaved-changes-dialog/unsaved-changes-dialog.component";
import { OrganisationMapService } from "@org-common/lib/architecture/organisation-map/organisation-map.service";
import { ConfigArea } from "@org-common/lib/configuration/configuration-types";
import { FeaturesService } from "@org-common/lib/features/features.service";
import { CommonTeamsService } from "@org-common/lib/teams/common-teams.service";
import { IWorkflowStepFooterTemplate } from "@org-common/lib/workflow/workflow-component-registry";
import { WorkflowStepComponentAdapter } from "@org-common/lib/workflow/workflow-step-component-adapter";
import { allTeamConfigGroups } from "app/platform/configuration/team/team-configuration-page/team-configuration-page-elements";
import { BehaviorSubject, EMPTY, lastValueFrom, Subject } from "rxjs";
import { catchError, map, switchMap, tap } from "rxjs/operators";
import { getTeamDefaultFeatures, IAddTeamWorkflowRunData, initialiseAddTeamWorkflowRunData, setTeamDefaultFeatures } from "./add-team-workflow";

// so the angular compiler doesn't cry when using ViewChild
@Directive()
export class AddTeamWorkflowStepBaseComponent extends WorkflowStepComponentAdapter {
    public runData!: IAddTeamWorkflowRunData;
    public workflowStepFooterTemplates = new Subject<IWorkflowStepFooterTemplate[]>();
    public submitting = false;
    public isValid = new BehaviorSubject<boolean>(false);
    public workflowStepCompleted = this.isValid.asObservable();
    public workflowStepNextStep = new Subject<void>();

    protected teamsService: CommonTeamsService;
    protected dialogService: AdaptCommonDialogService;
    protected rxjsBreezeService: RxjsBreezeService;
    protected userService: UserService;
    protected commonDataService: CommonDataService;
    protected featuresService: FeaturesService;
    protected orgMapService: OrganisationMapService;

    protected organisationConfigurationPageRoute?: IAdaptRoute<any> | null;

    @ViewChild("createTeamButton")
    public set createTeamButton(ref: TemplateRef<any>) {
        this.workflowStepFooterTemplates.next([
            { alignment: "right", template: ref },
        ]);
    }

    public constructor(injector: Injector) {
        super();

        this.organisationConfigurationPageRoute = injector.get(ORGANISATION_CONFIGURATION_PAGE, null);
        this.teamsService = injector.get(CommonTeamsService);
        this.dialogService = injector.get(AdaptCommonDialogService);
        this.rxjsBreezeService = injector.get(RxjsBreezeService);
        this.userService = injector.get(UserService);
        this.commonDataService = injector.get(CommonDataService);
        this.featuresService = injector.get(FeaturesService);
        this.orgMapService = injector.get(OrganisationMapService);
    }

    public get allEntitiesAreValid() {
        return this.runData.entitiesToConfirm.every((e) => e && e.entityAspect.validateEntity());
    }

    public get stepIsValid() {
        return this.allEntitiesAreValid && !this.submitting;
    }

    public get teamTypeName(): string | undefined {
        return this.runData?.team?.teamTypeConfiguration?.name;
    }

    public workflowStepOnInit() {
        const runData: IAddTeamWorkflowRunData = this.workflowStep?.workflow.runData;
        if (!runData || !runData.team) {
            throw new Error("Team needs to be provided to the workflow");
        }

        this.runData = initialiseAddTeamWorkflowRunData(runData);

        this.validateEntities();
    }

    public onEntitiesChanged(entities: IBreezeEntity[]) {
        this.runData.entitiesToConfirm = ArrayUtilities.addElementIfNotAlreadyExists(this.runData.entitiesToConfirm, ...entities)
            .filter((e) => !!e && !e.entityAspect?.entityState?.isDetached());

        this.runData.createTeamBindingModel = this.getCreateTeamBindingModel();

        this.runData.entitiesToConfirm.forEach((ent) => this.emitEntityChange(ent));
        this.validateEntities();
    }

    protected getTeamConfigItems() {
        return allTeamConfigGroups()
            .flatMap((group) => group.items)
            .filter((item) => !!item.featureName)
            .filter((item) => !item.matchTeamType || item.matchTeamType === this.runData.team.teamType);
    }

    protected getActiveTeamConfigItems() {
        return this.getTeamConfigItems()
            .filter((item) => this.featuresService.isFeatureActive(item.featureName!));
    }

    protected getTeamDefaultFeatures() {
        return getTeamDefaultFeatures(this.runData.team.teamType, this.runData.team.teamTypeConfiguration);
    }

    protected setTeamDefaultFeatures() {
        return setTeamDefaultFeatures(this.runData);
    }

    protected async onTeamTypeConfigurationChange(teamTypeConfiguration: TeamTypeConfiguration) {
        // can happen when discarding the dialog on the details page
        if (!teamTypeConfiguration) {
            return;
        }

        // value streams should be under Economic Engine zone by default
        const partialZoneLocation = { zone: Zone.EconomicEngine };
        if (teamTypeConfiguration.code === TeamTypeCode.ValueStream) {
            await this.addDefaultLocation(partialZoneLocation);
        } else {
            this.clearDefaultLocation(partialZoneLocation);
        }

        if (!this.runData.hasCustomisedFeatures) {
            this.setTeamDefaultFeatures();
        }

        this.onEntitiesChanged([this.runData.team]);
    }

    protected async addDefaultLocation(location: Partial<Pick<TeamLocation, "zone" | "parentTeam">>) {
        // already automatically added, skip
        if (this.runData.defaultLocation) {
            return;
        }

        // already added manually by user, skip
        const existingLocationEntity = this.runData.entitiesToConfirm
            .filter((e) => e instanceof TeamLocation)
            .find((e) => (!!location.zone && e.zone === location.zone)
                || (!!location.parentTeam && e.parentTeam === location.parentTeam));
        if (existingLocationEntity) {
            return;
        }

        let locationEntity: TeamLocation;
        if (location.zone) {
            locationEntity = await lastValueFrom(this.orgMapService.addTeamToZone(this.runData.team, location.zone));
        } else if (location.parentTeam) {
            locationEntity = await lastValueFrom(this.teamsService.addChildTeamForParentTeam(this.runData.team, location.parentTeam));
        } else {
            this.log.warn("Zone or ParentTeam must be specified for automaticallyAddLocation");
            return;
        }

        this.onEntitiesChanged([locationEntity]);
        this.runData.defaultLocation = locationEntity;
    }

    protected clearDefaultLocation(location: Partial<Pick<TeamLocation, "zone" | "parentTeam">>) {
        if (this.runData.defaultLocation &&
            ((!!location.zone && this.runData.defaultLocation.zone === location.zone)
                || (!!location.parentTeam && this.runData.defaultLocation.parentTeam === location.parentTeam))) {
            this.runData.defaultLocation.entityAspect.rejectChanges();
            this.onEntitiesChanged([this.runData.defaultLocation]);
            this.runData.defaultLocation = undefined;
        }
    }

    @Autobind
    public async onConfigureOrganisationGroupTypesClicked() {
        if (this.organisationConfigurationPageRoute) {
            const result = await lastValueFrom(this.dialogService.open(UnsavedChangesDialogComponent, {
                message: "<p>Leaving this page will discard all changes and cancel team creation.</p><p>Are you sure you want to continue?</p>",
                entitiesValid: false,
            }), { defaultValue: ChangeAction.Stay });

            if (result === ChangeAction.DiscardAndContinue) {
                await lastValueFrom(this.commonDataService.rejectChanges([this.runData.team, ...this.runData.entitiesToConfirm]));
                this.organisationConfigurationPageRoute.gotoRoute(undefined, { section: ConfigArea[ConfigArea.Team] }).subscribe();
            }
        }
    }

    private getCreateTeamBindingModel() {
        const team = this.runData.team;
        const bindingModel = this.runData.createTeamBindingModel ?? {};
        bindingModel.organisationId = team.organisationId;
        bindingModel.name = team.name;
        bindingModel.teamLeaderPersonId = team.teamLeaderPerson?.personId;
        bindingModel.teamType = team.teamType;
        bindingModel.isPrivate = team.isPrivate;
        bindingModel.customData = team.customData;
        bindingModel.teamTypeConfigurationId = team.teamTypeConfigurationId;

        const addedLocations = this.runData.entitiesToConfirm
            .filter((ent) => ent instanceof TeamLocation && ent.entityAspect.entityState.isAdded())
            .sort(SortUtilities.getSortByFieldFunction<TeamLocation>("ordinal")) as TeamLocation[];
        bindingModel.parentTeamIds = addedLocations
            .filter((tl) => !ObjectUtilities.isNullOrUndefined(tl.parentTeamId) && tl.team === team)
            .map((tl) => tl.parentTeamId!);
        bindingModel.childTeamIds = addedLocations
            .filter((tl) => tl.parentTeam === team)
            .map((tl) => tl.teamId);
        bindingModel.parentZones = addedLocations
            .filter((tl) => !ObjectUtilities.isNullOrUndefined(tl.zone))
            .map((tl) => tl.zone!);

        bindingModel.memberPersonIds = this.runData.createTeamBindingModel.memberPersonIds ?? [];
        bindingModel.teamFeatures = this.runData.createTeamBindingModel.teamFeatures ?? [];

        return bindingModel;
    }

    public validateEntities() {
        this.isValid.next(this.stepIsValid);
    }

    @Autobind
    public saveAndClose(immediate = false) {
        this.submitting = true;
        this.workflowStepIsBusy.next(true);

        // update the binding model again
        this.onEntitiesChanged([]);
        return this.teamsService.createTeam(this.runData.createTeamBindingModel).pipe(
            // reject all the temporary entities
            switchMap((newTeam) => this.commonDataService.rejectChanges([this.runData.team, ...this.runData.entitiesToConfirm]).pipe(
                // tell the org map we've removed the temp team
                tap(() => this.orgMapService.emitMapEntityChanged([this.runData.team])),
                map(() => newTeam),
            )),
            // override the old team with the new team so that we can pull it out later
            tap((newTeam) => this.runData.team = newTeam),
            tap(() => this.workflowStepNextStep.next()),
            catchError((err) => this.handleSaveError(err)),
            // changed from finalize as that will swallow error
            tap(() => {
                if (immediate) {
                    this.dialogService.closeAll();
                }
            }),
        );
    }

    private handleSaveError(err: unknown) {
        const message = ErrorHandlingUtilities.getHttpResponseMessage(err);
        this.workflowStepErrorMessage.next(message);

        this.submitting = false;
        this.workflowStepIsBusy.next(false);
        this.validateEntities();
        return EMPTY;
    }
}
